import { AdgroupFormData, CampaignFormData, ExternalType, promotionTypes } from '@tt4b/ttam-enums';
import {
  useAdgroupIos14,
  useAllowlist,
  useStdAdgroupCbo,
  useStdAdgroupIs,
  useStdObjectiveType,
  ValidationError,
} from '@tt4b/ttam-hooks';
import { Ref, computed, ComputedRef } from '@vue/composition-api';
import { i18n } from './../../../i18n';
import { TypeItem } from './types';

export function usePropsList(opt: { campaignData: Ref<CampaignFormData>; adgroupData: Ref<AdgroupFormData> }) {
  const { campaignData, adgroupData } = opt;
  const { isReach, isVideoView, isConversion, isProductSales } = useStdObjectiveType({
    campaignFormData: campaignData,
  });
  const { isBrandTrackDataWhite, isProductSalesOpenLoopAppWhite } = useAllowlist();
  const { isCLA } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isCreateNonFirstCBOAdgroup, firstCboAdgroupData } = useStdAdgroupCbo({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const list: ComputedRef<TypeItem[]> = computed(() => {
    const { app, website, video } = promotionTypes();
    let typeList = [website, app] as any;

    if ((isReach.value || isVideoView.value) && isBrandTrackDataWhite.value) {
      typeList = [video];
    }
    if (isProductSales.value) {
      // CLA只允许推广website，或者没有 app flow 的白名单
      if (isCLA.value || !isProductSalesOpenLoopAppWhite.value) {
        typeList = [website];
      }
      if (isCreateNonFirstCBOAdgroup.value) {
        typeList = typeList.map(item => {
          const curItem = { ...item };
          if (!item.value.includes(firstCboAdgroupData.value.externalType)) {
            curItem.disabled = true;
            curItem.disabledHint = 'cbo_adg_must_use_same_optimization_location';
          }
          return curItem;
        });
      }
    }
    return typeList;
  });

  return {
    list,
  };
}

export function usePropsVisible(opt: { campaignData: Ref<CampaignFormData> }) {
  const { isTraffic, isConversion, isReach, isVideoView, isProductSales } = useStdObjectiveType({
    campaignFormData: opt.campaignData,
  });
  const { isBrandTrackDataWhite } = useAllowlist();
  const visible = computed(
    () =>
      isProductSales.value ||
      isTraffic.value ||
      isConversion.value ||
      ((isReach.value || isVideoView.value) && isBrandTrackDataWhite.value),
  );
  return {
    visible,
  };
}

export function useInitDataGet(opt: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}): ExternalType {
  const { campaignData: campaignFormData, adgroupData } = opt;
  const {
    isProductSales,
    isConversion,
    isTraffic,
    isReach,
    isVideoView,
    isEngagement,
    isLeadAds,
  } = useStdObjectiveType({ campaignFormData });

  const { isDedicateCampaign } = useAdgroupIos14({
    campaignFormData,
    adgroupFormData: adgroupData,
  });

  // 其它 objective 的逻辑
  if (!isProductSales.value) {
    if (isTraffic.value) {
      return ExternalType.WEBSITE;
    } else if (isEngagement.value || isReach.value || isVideoView.value) {
      return ExternalType.EXTERNAL_OR_DISPLAY;
    } else if (isLeadAds.value) {
      return ExternalType.LEAD_ADS;
    }

    return ExternalType.NONE;
  }

  // product sales 的逻辑
  const { isLiveAds, isStandardAdsWithShop, isProductFromCatalog, isPSA, isStandardAdsWithShowcase } = useStdAdgroupIs({
    campaignFormData,
    adgroupFormData: adgroupData,
  });
  if (isLiveAds.value) {
    return ExternalType.SHOP_LIVE;
  } else if (isStandardAdsWithShop.value || isStandardAdsWithShowcase.value) {
    return ExternalType.SHOP_VIDEO;
  } else if (isProductFromCatalog.value) {
    return isDedicateCampaign.value ? ExternalType.IOS_APP : ExternalType.WEBSITE;
  } else if (isPSA.value) {
    return ExternalType.PSA_PRODUCT;
  } else {
    return ExternalType.NONE;
  }
}

export function useRules() {
  return {
    externalTypeValidator: computed(() => [
      {
        trigger: 'change',
        validator: (rule, value, callback, formData: AdgroupFormData) => {
          if (!formData.externalType) {
            return callback(
              new ValidationError({
                messageI18nKey: 'adgroup_contenttype_empty',
                message: i18n.t('adgroup_contenttype_empty'),
                moduleName: i18n.t('adgroup_promote_optimization_location'),
                subModuleName: i18n.t('adgroup_promote_optimization_location'),
              }),
            );
          }
          return callback();
        },
      },
    ]),
  };
}
